import React, { useState, useEffect, useCallback, memo } from 'react';
import apiService from '../api/apiService';
import { Plus } from 'lucide-react';

const ImageWithLoading = memo(({ src, alt, className }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isImageVisible, setIsImageVisible] = useState(false);

  const handleImageLoad = useCallback(() => {
    setIsImageVisible(true);
    setTimeout(() => setIsLoading(false), 300);
  }, []);

  return (
    <div className="relative w-full h-full bg-gray-100">
      {isLoading && (
        <div className="absolute inset-0 z-20 flex items-center justify-center">
          <div 
            className="w-8 h-8 border-4 rounded-full border-amber-500 border-t-transparent animate-spin" 
          />
        </div>
      )}
      <img
        src={src}
        alt={alt}
        className={`${className} relative z-10 transition-opacity duration-300 ${
          isImageVisible ? 'opacity-100' : 'opacity-0'
        }`}
        onLoad={handleImageLoad}
      />
    </div>
  );
});

const AddToOrderButton = memo(({ onAddToOrder, item, isMobile }) => {
  const handleClick = useCallback((e) => {
    e.preventDefault();
    onAddToOrder(item);
  }, [onAddToOrder, item]);

  if (isMobile) {
    return (
      <button
        onClick={handleClick}
        className="absolute z-30 flex items-center justify-center w-10 h-10 text-white transform translate-x-1/2 bg-white border-2 border-gray-100 rounded-full shadow-lg -bottom-2 right-4 hover:scale-105"
        aria-label="Add to order"
      >
        <Plus className="w-6 h-6 text-black" />
      </button>
    );
  }

  return (
    <button
      onClick={handleClick}
      className="flex items-center justify-center w-full gap-2 px-3 py-2 text-sm font-medium text-white transition-all rounded-lg bg-amber-500 hover:bg-amber-600"
    >
      <Plus className="w-4 h-4" />
      Add to Order
    </button>
  );
});

AddToOrderButton.displayName = 'AddToOrderButton';
ImageWithLoading.displayName = 'ImageWithLoading';

const MobileCard = memo(({ item, onAddToOrder }) => (
  <div className="flex justify-between w-full py-4 border-b border-gray-60">
    <div className="flex flex-col flex-grow pr-4">
      <h3 className="text-lg font-semibold text-gray-900">{item.label}</h3>
      <div className="mt-1 text-sm text-gray-500">
        ${item.price.toFixed(2)}
      </div>
      <p className="mt-2 text-sm text-gray-600">
        {item.description}
      </p>
    </div>

    <div className="relative flex-shrink-0">
      <div className="relative w-32 h-32 overflow-hidden rounded-xl">
        <ImageWithLoading
          src={item.imageUrl || item.image}
          alt={item.label}
          className="object-cover w-full h-full"
        />
      </div>
      <AddToOrderButton onAddToOrder={onAddToOrder} item={item} isMobile={true} />
    </div>
  </div>
));

const DesktopCard = memo(({ item, onAddToOrder }) => (
  <div className="group flex flex-col h-[380px] bg-white shadow-md rounded-2xl overflow-hidden">
    <div className="relative aspect-[4/3] overflow-hidden">
      <ImageWithLoading
        src={item.imageUrl || item.image}
        alt={item.label}
        className="object-cover w-full h-full transition-transform duration-700 group-hover:scale-110"
      />
      <div className="absolute z-10 top-3 left-3">
        <div className="px-3 py-1.5 backdrop-blur-md rounded-full transition-all duration-300 group-hover:bg-amber-500 group-hover:shadow-lg bg-white/90 shadow-md">
          <span className="text-sm font-bold transition-colors duration-300 group-hover:text-white text-amber-900">
            ${item.price.toFixed(2)}
          </span>
        </div>
      </div>
    </div>

    <div className="flex flex-col flex-grow p-3">
      <h3 className="text-lg font-bold leading-tight text-amber-900">
        {item.label}
      </h3>
      <div
        className="mt-1 text-sm text-amber-600 overflow-y-auto scrollbar-thin scrollbar-thumb-amber-500 scrollbar-track-amber-100 h-[80px]"
        title={item.description}
      >
        {item.description}
      </div>
      <div className="mt-auto">
        <AddToOrderButton onAddToOrder={onAddToOrder} item={item} isMobile={false} />
      </div>
    </div>
  </div>
));

MobileCard.displayName = 'MobileCard';
DesktopCard.displayName = 'DesktopCard';

export const FoodCard = memo(({ item, onAddToOrder }) => {
  return (
    <div className="w-full md:w-[calc(25%-12px)] mb-3">
      <div className="block md:hidden">
        <MobileCard item={item} onAddToOrder={onAddToOrder} />
      </div>
      <div className="hidden md:block">
        <DesktopCard item={item} onAddToOrder={onAddToOrder} />
      </div>
    </div>
  );
});

FoodCard.displayName = 'FoodCard';


const CategorySection = memo(({ category, items, onAddToOrder }) => {
  const sectionId = React.useMemo(() => 
    `category-${category.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`,
    [category.label]
  );

  return (
    <section id={sectionId} className="mb-12 scroll-mt-32">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-amber-900 md:text-3xl">{category.label}</h2>
      </div>
      <div className="flex flex-wrap gap-3 md:gap-4">
        {items.map((item) => (
          <FoodCard 
            key={item._id} 
            item={item} 
            onAddToOrder={onAddToOrder} 
          />
        ))}
      </div>
    </section>
  );
});

CategorySection.displayName = 'CategorySection';

const FoodSection = memo(({ onAddToOrder }) => {
  const [categories, setCategories] = useState([]);
  const [categoryItems, setCategoryItems] = useState({});
  const [loading, setLoading] = useState(true);

  // Memoize fetchData function
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const categoriesData = await apiService.getCategories();
      
      // Fetch all items in parallel
      const itemsPromises = categoriesData.map(async (category) => {
        const items = await apiService.getItemsByCategory(category._id);
        return { categoryId: category._id, items };
      });

      const itemsResults = await Promise.all(itemsPromises);
      const itemsMap = itemsResults.reduce((acc, { categoryId, items }) => {
        acc[categoryId] = items;
        return acc;
      }, {});

      setCategories(categoriesData);
      setCategoryItems(itemsMap);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Memoize the loading component
  const LoadingComponent = memo(() => (
    <div className="flex items-center justify-center min-h-screen">
      <div className="flex flex-col items-center space-y-4">
        <div className="w-12 h-12 border-4 rounded-full border-amber-500 border-t-transparent animate-spin"></div>
        <p className="text-lg font-medium text-amber-900">Loading menu...</p>
      </div>
    </div>
  ));

  LoadingComponent.displayName = 'LoadingComponent';

  if (loading) {
    return <LoadingComponent />;
  }

  return (
    <div className="flex flex-col pt-[120px] pb-[80px] md:pt-[140px] md:pb-[100px]">
      <div className="max-w-[1200px] mx-auto px-4 md:px-6">
        <div className="space-y-12">
          {categories.map((category) => (
            <CategorySection
              key={category._id}
              category={category}
              items={categoryItems[category._id] || []}
              onAddToOrder={onAddToOrder}
            />
          ))}
        </div>
      </div>
    </div>
  );
});

FoodSection.displayName = 'FoodSection';

export default FoodSection;